import {
  getFieldId,
  getFieldType,
  iterateGridRows,
} from './formUseCase';

/**
 * @typedef {{ kind: 'field', id: string, rules: ComputedValueRule[], field: FormItem }} FieldComputedTarget
 * @typedef {{ kind: 'cell', rowId: string, colId: string, rules: ComputedValueRule[], cellType: string, tags: string[] }} CellComputedTarget
 * @typedef {FieldComputedTarget | CellComputedTarget} ComputedTarget
 */

/**
 * @typedef {{ kind: 'field', id: string, rowId: string | null, colId: string | null }} TagFieldEntry
 * @typedef {{ kind: 'cell', rowId: string, colId: string }} TagCellEntry
 * @typedef {TagFieldEntry | TagCellEntry} TagEntry
 */

/**
 * @typedef {object} ComputedGraph
 * @property {Map<string, ComputedTarget>} targets
 * @property {Map<string, Set<string>>} dependents
 * @property {Map<string, Set<string>>} tagDependents
 * @property {Map<string, TagEntry[]>} tagIndex
 * @property {Map<string, Set<string>>} fieldsByColId
 * @property {Map<string, string>} fieldColIdByFieldId
 * @property {Map<string, string>} fieldRowIdByFieldId
 */

/**
 * @param {string} id
 * @returns {string}
 */
export function fieldTargetKey(id) {
  return `field:${id}`;
}

/**
 * @param {string} rowId
 * @param {string} colId
 * @returns {string}
 */
export function cellTargetKey(rowId, colId) {
  return `cell:${rowId}::${colId}`;
}

/**
 * @param {string} key
 * @returns {string | null}
 */
export function targetKeyToVisitedId(key) {
  if (key.startsWith('field:')) return key.slice('field:'.length);
  return key;
}

/**
 * @param {string} rowId
 * @param {string} colId
 * @returns {string}
 */
export function cellValueKey(rowId, colId) {
  return `${rowId}::${colId}`;
}

/**
 * @typedef {object} RuleReadSources
 * @property {Set<string>} fieldRefs
 * @property {Set<string>} tags
 * @property {Set<string>} filterColIds
 */

/**
 * @param {Expr} expr
 * @param {RuleReadSources} out
 */
function collectExprReadSources(expr, out) {
  if (!expr) return;
  if (expr.fieldRef) {
    out.fieldRefs.add(expr.fieldRef);
  }
  if (expr.tagRef) {
    out.tags.add(expr.tagRef.tag);
    if (expr.tagRef.filter?.colId) {
      out.filterColIds.add(expr.tagRef.filter.colId);
    }
  }
  if (expr.operation?.args) {
    expr.operation.args.forEach((arg) => {
      collectExprReadSources(arg, out);
    });
  }
}

/**
 * @param {ComputedValueRule} rule
 * @returns {RuleReadSources}
 */
function getRuleReadSources(rule) {
  /** @type {RuleReadSources} */
  const out = {
    fieldRefs: new Set(),
    tags: new Set(),
    filterColIds: new Set(),
  };
  (rule.when ?? []).forEach((cond) => {
    out.fieldRefs.add(cond.id);
  });
  collectExprReadSources(rule.expression, out);
  return out;
}

/**
 * @param {ComputedValueRule} rule
 * @param {(sourceId: string) => void} addDependent
 * @param {(tag: string) => void} addTagDependent
 */
function registerRuleTriggerDeps(rule, addDependent, addTagDependent) {
  const read = getRuleReadSources(rule);
  const triggers = rule.triggers ?? [];

  read.filterColIds.forEach((colId) => {
    addDependent(`__col__:${colId}`);
  });

  if (!triggers.length) {
    read.fieldRefs.forEach((id) => addDependent(id));
    read.tags.forEach((tag) => addTagDependent(tag));
    return;
  }

  triggers.forEach((trigger) => {
    if (trigger.fieldRef && read.fieldRefs.has(trigger.fieldRef)) {
      addDependent(trigger.fieldRef);
    }
    if (trigger.tagRef && read.tags.has(trigger.tagRef)) {
      addTagDependent(trigger.tagRef);
    }
  });
}

/**
 * @param {ComputedValueRule[]} rules
 * @param {(sourceId: string) => void} addDependent
 * @param {(tag: string) => void} addTagDependent
 */
function collectRuleDependencies(rules, addDependent, addTagDependent) {
  (rules ?? []).forEach((rule) => {
    registerRuleTriggerDeps(rule, addDependent, addTagDependent);
  });
}

/**
 * @param {ApiFormResponse} formConfig
 * @returns {ComputedGraph}
 */
export function buildComputedGraph(formConfig) {
  /** @type {Map<string, ComputedTarget>} */
  const targets = new Map();
  /** @type {Map<string, Set<string>>} */
  const dependents = new Map();
  /** @type {Map<string, Set<string>>} */
  const tagDependents = new Map();
  /** @type {Map<string, TagEntry[]>} */
  const tagIndex = new Map();
  /** @type {Map<string, Set<string>>} */
  const fieldsByColId = new Map();
  /** @type {Map<string, string>} */
  const fieldColIdByFieldId = new Map();
  /** @type {Map<string, string>} */
  const fieldRowIdByFieldId = new Map();

  /**
   * @param {string} sourceId
   * @param {string} targetKey
   */
  const addDependent = (sourceId, targetKey) => {
    if (!dependents.has(sourceId)) dependents.set(sourceId, new Set());
    dependents.get(sourceId)?.add(targetKey);
  };

  /**
   * @param {string} tag
   * @param {string} targetKey
   */
  const addTagDependent = (tag, targetKey) => {
    if (!tagDependents.has(tag)) tagDependents.set(tag, new Set());
    tagDependents.get(tag)?.add(targetKey);
  };

  /**
   * @param {string} tag
   * @param {TagEntry} entry
   */
  const registerTag = (tag, entry) => {
    if (!tagIndex.has(tag)) tagIndex.set(tag, []);
    tagIndex.get(tag)?.push(entry);
  };

  /**
   * @param {string} targetKey
   * @param {ComputedValueRule[]} rules
   */
  const registerTargetDeps = (targetKey, rules) => {
    const addDep = (sourceId) => addDependent(sourceId, targetKey);
    const addTagDep = (tag) => addTagDependent(tag, targetKey);
    collectRuleDependencies(rules, addDep, addTagDep);
  };

  formConfig.tabs.forEach((tab) => {
    tab.fieldsets.forEach((fieldset) => {
      fieldset.fields.forEach((field) => {
        const textData = field.text;
        if (textData?.computedValue?.length) {
          const { id } = textData;
          const key = fieldTargetKey(id);
          targets.set(key, {
            kind: 'field',
            id,
            rules: textData.computedValue,
            field,
          });
          registerTargetDeps(key, textData.computedValue);
          (textData.tags ?? []).forEach((tag) => {
            registerTag(tag, {
              kind: 'field', id, rowId: null, colId: null,
            });
          });
        }

        if (getFieldType(field) !== 'grid') return;
        iterateGridRows(field.grid?.table?.groups ?? [], (row) => {
          const rowId = row.rowId ?? null;
          (row.cells ?? []).forEach((cell) => {
            if (cell.text) {
              const {
                colId, tags, computedValue, type,
              } = cell.text;
              (tags ?? []).forEach((tag) => {
                registerTag(tag, {
                  kind: 'cell',
                  rowId: cell.text.rowId,
                  colId,
                });
              });
              if (computedValue?.length && rowId) {
                const key = cellTargetKey(rowId, colId);
                targets.set(key, {
                  kind: 'cell',
                  rowId,
                  colId,
                  rules: computedValue,
                  cellType: type,
                  tags: tags ?? [],
                });
                registerTargetDeps(key, computedValue);
              }
            }
            const formItem = cell.field?.formItem;
            if (!formItem) return;
            const innerId = getFieldId(formItem);
            const colId = cell.field?.colId;
            if (!innerId || !colId) return;
            fieldColIdByFieldId.set(innerId, colId);
            if (rowId) fieldRowIdByFieldId.set(innerId, rowId);
            if (!fieldsByColId.has(colId)) fieldsByColId.set(colId, new Set());
            fieldsByColId.get(colId)?.add(innerId);

            const innerText = formItem.text;
            if (innerText?.tags?.length) {
              innerText.tags.forEach((tag) => {
                registerTag(tag, {
                  kind: 'field',
                  id: innerId,
                  rowId,
                  colId,
                });
              });
            }
            if (innerText?.computedValue?.length) {
              const key = fieldTargetKey(innerId);
              if (!targets.has(key)) {
                targets.set(key, {
                  kind: 'field',
                  id: innerId,
                  rules: innerText.computedValue,
                  field: formItem,
                });
              }
              registerTargetDeps(key, innerText.computedValue);
            }
          });
        });
      });
    });
  });

  // Wire colId pseudo-dependencies to real field ids
  fieldsByColId.forEach((fieldIds, colId) => {
    const pseudoKey = `__col__:${colId}`;
    const pseudoDeps = dependents.get(pseudoKey);
    if (!pseudoDeps) return;
    fieldIds.forEach((fieldId) => {
      if (!dependents.has(fieldId)) dependents.set(fieldId, new Set());
      pseudoDeps.forEach((targetKey) => {
        dependents.get(fieldId)?.add(targetKey);
      });
    });
    dependents.delete(pseudoKey);
  });

  return {
    targets,
    dependents,
    tagDependents,
    tagIndex,
    fieldsByColId,
    fieldColIdByFieldId,
    fieldRowIdByFieldId,
  };
}

/**
 * @param {ApiFormResponse} formConfig
 * @returns {Record<string, string>}
 */
export function extractInitialCellValues(formConfig) {
  /** @type {Record<string, string>} */
  const initial = {};
  formConfig.tabs.forEach((tab) => {
    tab.fieldsets.forEach((fieldset) => {
      fieldset.fields.forEach((field) => {
        if (getFieldType(field) !== 'grid') return;
        iterateGridRows(field.grid?.table?.groups ?? [], (row) => {
          (row.cells ?? []).forEach((cell) => {
            if (cell.text) {
              const { rowId, colId, value } = cell.text;
              if (rowId && colId) {
                initial[cellValueKey(rowId, colId)] = value ?? '';
              }
            }
          });
        });
      });
    });
  });
  return initial;
}

/**
 * @param {string[]} tags
 * @param {ComputedGraph} graph
 * @returns {string[]}
 */
export function getTargetKeysForTags(tags, graph) {
  /** @type {Set<string>} */
  const keys = new Set();
  tags.forEach((tag) => {
    (graph.tagDependents.get(tag) ?? []).forEach((k) => keys.add(k));
  });
  return [...keys];
}
